<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <ion-item
    button
    class="workspace-list-item no-padding-end"
    :detail="false"
    :class="{
      'workspace-list-item--hovered': isHovered || menuOpened,
      'workspace-list-item--hidden': isHidden || workspace.isArchived,
    }"
    @click="$emit('click', workspace, $event)"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @contextmenu.stop="onOptionsClick"
  >
    <div class="workspace-list-item-content">
      <div
        v-show="!workspace.isArchived"
        class="list-item-column workspace-favorite-icon"
        :class="{
          'workspace-favorite-icon__on': isFavorite,
          'workspace-favorite-icon__off': !isFavorite,
        }"
        @click.stop="$emit('favoriteClick', workspace, $event)"
      >
        <ion-icon :icon="star" />
      </div>
      <ion-icon
        v-if="!workspace.isArchived"
        class="cloud-overlay"
        :class="workspace.availableOffline ? 'cloud-overlay-ok' : 'cloud-overlay-ko'"
        :icon="workspace.availableOffline ? cloudDone : cloudOffline"
      />
      <!-- workspace name -->
      <div
        class="list-item-column workspace-name"
        :title="workspace.name"
      >
        <ion-text class="workspace-name__label title-h4">
          {{ workspace.name }}
        </ion-text>
      </div>
      <ion-icon
        v-if="workspace.isArchived"
        class="list-item-column workspace-archive"
        :icon="archive"
      />
      <div
        class="list-item-column workspace-hidden"
        v-if="isHidden && !workspace.isArchived"
      >
        <ion-icon
          class="workspace-hidden__icon"
          :icon="eyeOff"
        />
        <ion-text class="subtitles-sm workspace-hidden__text">{{ $msTranslate('WorkspacesPage.Workspace.hidden') }}</ion-text>
      </div>

      <!-- role user -->
      <div class="list-item-column workspace-role">
        <workspace-role-tag
          v-if="!workspace.isArchived && windowWidth > WindowSizeBreakpoints.XS"
          :role="workspace.selfRole"
          class="workspace-role-tag"
        />
        <ion-label
          v-if="workspace.isArchived"
          class="archived-label"
        >
          <ion-text class="button-small archived-label-text">
            {{ $msTranslate('WorkspacesPage.archiveWorkspace.readOnly') }}
          </ion-text>
        </ion-label>
        <workspace-missing-ownership
          class="missing-ownership"
          v-if="workspace.canSelfPromoteToOwner"
          :show-text="isLargeDisplay && workspace.canSelfPromoteToOwner"
          @click.stop="$emit('selfPromoteClick', workspace, $event)"
        />
      </div>

      <!-- user avatars -->
      <div
        class="list-item-column workspace-users"
        v-show="clientProfile !== UserProfile.Outsider"
        v-if="isLargeDisplay && windowWidth >= WindowSizeBreakpoints.MD && !workspace.isArchived"
      >
        <avatar-group
          v-show="workspace.sharing.length > 0"
          class="shared-group"
          :people="workspace.sharing.map((item) => item[0].humanHandle.label)"
          :max-display="2"
          @click.stop="$emit('shareClick', workspace, $event)"
        />
        <ion-label
          class="not-shared-label cell"
          v-show="workspace.sharing.length === 0"
          @click.stop="$emit('shareClick', workspace, $event)"
        >
          {{ $msTranslate('WorkspacesPage.Workspace.notShared') }}
        </ion-label>
      </div>

      <!-- last update -->
      <div
        class="list-item-column workspace-last-update"
        v-show="workspace.isArchived"
      >
        <ion-label class="list-item-label label-last-update cell">
          {{ $msTranslate(formatTimeSince(workspace.lastUpdated, '--', 'short')) }}
        </ion-label>
      </div>

      <!-- workspace size -->
      <div
        class="list-item-column workspace-size"
        v-show="false"
      >
        <ion-label class="label-size cell">
          {{ $msTranslate(formatFileSize(workspace.size)) }}
        </ion-label>
      </div>

      <!-- options -->
      <div class="list-item-end workspace-options">
        <ion-button
          fill="clear"
          class="options-button"
          @click.stop="$emit('shareClick', workspace, $event)"
          v-if="windowWidth < WindowSizeBreakpoints.MD"
        >
          <ion-icon
            :icon="shareSocial"
            slot="icon-only"
            class="options-button__icon"
          />
        </ion-button>
        <ion-button
          fill="clear"
          class="options-button"
          @click.stop="onOptionsClick($event)"
        >
          <ion-icon
            :icon="ellipsisHorizontal"
            slot="icon-only"
            class="options-button__icon"
          />
        </ion-button>
      </div>
    </div>
  </ion-item>
</template>

<script setup lang="ts">
import { formatFileSize } from '@/common/file';
import AvatarGroup from '@/components/workspaces/AvatarGroup.vue';
import WorkspaceMissingOwnership from '@/components/workspaces/WorkspaceMissingOwnership.vue';
import WorkspaceRoleTag from '@/components/workspaces/WorkspaceRoleTag.vue';
import { UserProfile, WorkspaceInfo } from '@/parsec';
import { IonButton, IonIcon, IonItem, IonLabel, IonText } from '@ionic/vue';
import { archive, cloudDone, cloudOffline, ellipsisHorizontal, eyeOff, shareSocial, star } from 'ionicons/icons';
import { formatTimeSince, useWindowSize, WindowSizeBreakpoints } from 'megashark-lib';
import { ref } from 'vue';

const isHovered = ref(false);
const menuOpened = ref(false);
const { isLargeDisplay, windowWidth } = useWindowSize();

const props = defineProps<{
  workspace: WorkspaceInfo;
  clientProfile: UserProfile;
  isFavorite: boolean;
  isHidden: boolean;
}>();

const emits = defineEmits<{
  (e: 'click', workspace: WorkspaceInfo, event?: Event): void;
  (e: 'favoriteClick', workspace: WorkspaceInfo, event?: Event): void;
  (e: 'menuClick', workspace: WorkspaceInfo, event: Event, onFinished: () => void): void;
  (e: 'shareClick', workspace: WorkspaceInfo, event?: Event): void;
  (e: 'selfPromoteClick', workspace: WorkspaceInfo, event?: Event): void;
}>();

async function onOptionsClick(event: Event): Promise<void> {
  event.preventDefault();
  menuOpened.value = true;
  emits('menuClick', props.workspace, event, () => {
    menuOpened.value = false;
  });
}
</script>

<style lang="scss" scoped>
.workspace-list-item {
  cursor: pointer;
  text-align: center;
  user-select: none;
  border-radius: var(--parsec-radius-12);
  border: 1px solid var(--parsec-color-light-secondary-disabled);
  --background: var(--parsec-color-light-secondary-white);
  width: 100%;
  overflow: hidden;
  transition: all 0.15s ease-in-out;

  &::part(native) {
    cursor: pointer !important;
    width: -webkit-fill-available;
    padding-left: 0;
    margin: 0.25rem;
    --background-hover: var(--parsec-color-light-secondary-white);
  }

  &-content {
    display: flex;
    background: var(--parsec-color-light-secondary-premiere);
    border-radius: var(--parsec-radius-8);
    align-items: center;
    width: 100%;
    height: 3rem;
  }

  .list-item-column {
    cursor: pointer !important;
  }

  &:hover {
    box-shadow: var(--parsec-shadow-input);

    .workspace-list-item-content {
      background: var(--parsec-color-light-primary-30);
    }
  }
}

.workspace-favorite-icon {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  padding: 0.25rem;
  margin-left: 0.5rem;
  border-radius: var(--parsec-radius-6);
  transition: color 150ms ease-out;
  flex-shrink: 0;
  position: relative;
  z-index: 20;

  &__on {
    color: var(--parsec-color-light-primary-600);

    &:hover {
      background: var(--parsec-color-light-primary-50);
      color: var(--parsec-color-light-primary-700);
    }
  }

  &__off {
    color: var(--parsec-color-light-secondary-disabled);

    &:hover {
      background: var(--parsec-color-light-primary-50);
      color: var(--parsec-color-light-primary-600);
    }
  }
}

.cloud-overlay {
  position: sticky;
  margin-left: 0.75rem;
  left: 0;
  display: flex;
  align-items: center;
  right: 0;
  font-size: 1rem;
  bottom: 1px;
  padding: 2px;
  background: var(--parsec-color-light-secondary-white);
  border-radius: 50%;
  flex-shrink: 0;

  &-ok {
    color: var(--parsec-color-light-primary-500);
  }

  &-ko {
    color: var(--parsec-color-light-secondary-text);
  }
}

.workspace-archive {
  font-size: 7rem;
  color: var(--parsec-color-light-secondary-disabled);
  z-index: 0;
}

.workspace-name {
  padding-inline: 0.5rem 1rem;
  width: 100%;
  height: 100%;
  min-width: 15rem;
  max-width: 45vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @include ms.responsive-breakpoint('lg') {
    min-width: 10rem;
  }

  @include ms.responsive-breakpoint('sm') {
    min-width: 5rem;
  }

  &__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    color: var(--parsec-color-light-secondary-text);
    min-width: 0;
  }
  z-index: 1;
}

.workspace-hidden {
  background: var(--parsec-color-light-secondary-white);
  color: var(--parsec-color-light-secondary-contrast);
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 3px 0.5rem;
  border-radius: var(--parsec-radius-8);
  flex-shrink: 0;
  width: 5rem;

  &__icon {
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  &__text {
    @include ms.responsive-breakpoint('xs') {
      display: none;
    }
  }
}

.workspace-role {
  min-width: 8rem;
  width: 100%;
  height: 100%;
  max-width: 15vw;
  flex-grow: 2;
  position: relative;
  z-index: 10;
  gap: 0.5rem;

  .workspace-role-tag {
    border: 1px solid var(--parsec-color-light-secondary-medium);
    border-radius: var(--parsec-radius-18);
    background: var(--parsec-color-light-secondary-white);
  }

  @include ms.responsive-breakpoint('lg') {
    min-width: 13rem;
    max-width: 11rem;
  }

  @include ms.responsive-breakpoint('md') {
    min-width: 11rem;
    max-width: 11rem;
  }

  @include ms.responsive-breakpoint('sm') {
    padding-inline: 0.125rem;
    min-width: 5rem;
    max-width: 8rem;
  }

  .archived-label {
    color: var(--parsec-color-light-secondary-text);
    display: flex;
    align-items: center;
    border: 1px solid var(--parsec-color-light-secondary-medium);
    border-radius: var(--parsec-radius-18);
    background: var(--parsec-color-light-secondary-white);

    &-text {
      background-color: none;
      padding: 0.2rem 0.5rem;
      color: var(--parsec-color-light-primary-500);
    }
  }

  .missing-ownership {
    flex-shrink: 0;
    left: 0;
    height: fit-content;
  }

  &:has(.missing-ownership) {
    .workspace-role-tag {
      display: none;

      @include ms.responsive-breakpoint('sm') {
        display: flex;
      }
    }
  }
}

.workspace-users {
  min-width: 10rem;
  max-width: 15rem;
  flex-grow: 0;
  overflow: visible;

  .shared-group {
    padding: 0.25rem;
    transition: background 0.2s ease-in-out;

    &:hover {
      background: var(--parsec-color-light-primary-100);
    }
  }

  .not-shared-label {
    color: var(--parsec-color-light-secondary-grey);
    padding: 0.375rem 0.5rem;
    border-radius: var(--parsec-radius-6);
    transition: background 0.15s ease-in-out;
    flex-grow: 0 !important;
    position: relative;
    z-index: 2;

    &:hover {
      background: var(--parsec-color-light-primary-100);
      color: var(--parsec-color-light-primary-700) !important;
    }
  }
}

.workspace-last-update {
  min-width: 11.25rem;
  flex-grow: 0;
}

.workspace-size {
  max-width: 7.5rem;
}

.workspace-options {
  flex-grow: 0;
  margin-left: auto;
  align-items: center;
  flex-shrink: 0;
  display: flex;
  gap: 0.5rem;
  position: relative;
  z-index: 8;

  ion-button::part(native) {
    padding: 0;
  }

  .options-button {
    --background-hover: none;
    background: none;
    border-radius: var(--parsec-radius-8);
    padding: 0.375rem;

    &__icon {
      color: var(--parsec-color-light-secondary-grey);
      font-size: 1.25rem;
      flex-shrink: 0;
    }

    &:hover {
      background: var(--parsec-color-light-secondary-disabled);

      .options-button__icon {
        color: var(--parsec-color-light-primary-500);
      }
    }
  }
}

.workspace-list-item--hidden {
  border: 1px solid var(--parsec-color-light-secondary-medium);

  .workspace-list-item-content {
    opacity: 0.9;
    filter: brightness(0.85);
  }

  &:hover {
    .workspace-list-item-content {
      background: var(--parsec-color-light-secondary-premiere);
    }
  }

  .workspace-name {
    max-width: calc(45vw - 5rem);
  }
}
</style>
